import { domAnimation, LazyMotion, m } from 'framer-motion'
import Link from 'next/link'
import React from 'react'

import { uniqueId } from '@/components/home/Contents'
import { cn } from '@/lib/utils'
import { ParsedContent, PrimaryImageProps } from '@/types/global'
import { PrimaryImage } from '@/v1/components/ui'
import { Routes } from '@/v1/constants/routes'

interface ContentProps {
  title: ParsedContent
  description: ParsedContent
  children?: React.ReactNode
}

interface BannerContentProps extends ContentProps {
  isLargeDevice?: boolean
}

interface Props {
  info: {
    title: ParsedContent
    description: ParsedContent
    mobileClassName?: string
    desktopClassName?: string
    desktopContentWrClassName?: string
    leftImage?: PrimaryImageProps
    rightImage?: PrimaryImageProps
    callToAction?: {
      text?: string
      url?: string
      className?: string
      wrClassName?: string
      available?: boolean
    }
  }
  children?: React.ReactNode
}

export const PrimaryBanner = ({ info, children }: Props) => {
  const {
    title,
    description,
    leftImage,
    rightImage,
    desktopClassName,
    mobileClassName,
    desktopContentWrClassName,
    callToAction
  } = info

  const renderCallToAction = () => {
    if (!!callToAction?.available) {
      return (
        <>
          <div className={cn('flex justify-center mt-6 md-mn:mt-[60px]', callToAction?.wrClassName)}>
            <Link
              href={callToAction?.url ?? Routes.BOOK_STORAGE_FACILITY}
              target={'_blank'}
              className={cn('link-like-button', callToAction?.className)}
            >
              {callToAction?.text ?? 'Store to Save Cost'}
            </Link>
          </div>
        </>
      )
    }
  }

  return (
    <>
      <section className={cn(`primary-banner-wrapper md-mx:hidden`, desktopClassName)}>
        <div className='main-banner'>
          {!!leftImage && (
            <PrimaryImage image={leftImage} className={'banner-image-left xl-mx:hidden flex items-end'} />
          )}
          <div className={cn('container mx-auto pt-24 pb-28', desktopContentWrClassName)}>
            <div className='row sm-mx:flex-col-reverse'>
              <div className='w-full text-center'>
                <BannerContent isLargeDevice title={title} description={description}>
                  {renderCallToAction()}
                  {children}
                </BannerContent>
              </div>
            </div>
          </div>
          {!!rightImage && (
            <PrimaryImage image={rightImage} className={'banner-image-right xl-mx:hidden flex items-end'} />
          )}
        </div>
      </section>

      <section className={cn('mobile-banner-content-section md-mn:hidden section-spacing--small', mobileClassName)}>
        <div className='container mx-auto'>
          <div className='row sm-mx:flex-col-reverse'>
            <div className='w-full text-center'>
              <BannerContent title={title} description={description}>
                {renderCallToAction()}
                {children}
              </BannerContent>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export const BannerContent: React.FC<BannerContentProps> = ({
  title,
  description,
  isLargeDevice = false,
  children
}) => {
  if (isLargeDevice) {
    return (
      <LazyMotion features={domAnimation}>
        <m.div
          key={uniqueId('animation-')}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          transition={{ duration: 0.7 }}
          initial={{ scale: 0.8, y: 100, opacity: 0 }}
          className='banner-content text-center'
        >
          <h1 className={cn('text-6xl font-bold text-secondary pb-4 capitalize', title?.classes)}>{title?.html}</h1>
          <h2 className={cn('text-2xl font-normal text-blue-river', description?.classes)}>{description?.html}</h2>
          {children}
        </m.div>
      </LazyMotion>
    )
  }

  return (
    <div className='w-full text-center'>
      <div className='banner-content text-center'>
        <h2
          className={cn(
            'capitalize text-4xl2 font-bold text-secondary mb-4 sm-mx:text-2xl26 sm-mx:mb-1',
            title?.classes
          )}
        >
          {title?.html}
        </h2>
        <h3 className={cn('text-2xl font-normal text-gray-400 mb-1 sm-mx:text-base', description?.classes)}>
          {description?.html}
        </h3>
        {children}
      </div>
    </div>
  )
}
